import { ItemStatusSliceType } from "../../../services/redux/slices/itemStatusSlice";
import { OrderTypesEnum } from "../../enums";
import envConfig from "../../env.config";
import { ItemType, SubItemType } from "../../types";
import { getDisabledItemsPerOrderType } from "../orderType/orderType";
import * as itemDetailsHelp from "./itemDetails";
import {
  getItemOfferOutOfStockAndCustomDeliveryTime,
  handleItemOfferPrice,
  handleSubItemOfferPrice,
} from "./itemDetailsHelper";
const lodashClonedeep = require("lodash.clonedeep");

export const reCheckItemDetails = (
  itemStatus: ItemStatusSliceType,
  itemObj: ItemType,
  checkIfDeletedItemOrSubItem?: boolean,
  hasRoundingMethodAndLevel: boolean = false
) => {
  const data = {
    item: { ...itemObj },
    isOutOfStock: false,
    customDeliveryTime: 0,
  };

  const { offerItem, isOutOfStock, customDeliveryTime } =
    getItemOfferOutOfStockAndCustomDeliveryTime(itemStatus, itemObj.id);
  data.isOutOfStock = isOutOfStock;

  handleItemOfferPrice(data.item, offerItem, hasRoundingMethodAndLevel);

  if (
    checkIfDeletedItemOrSubItem &&
    (data.item.deletedAt ||
      data.item.subItems[0].deletedAt ||
      !data.item.itemCategories ||
      data.item.itemCategories.length === 0)
  ) {
    data.isOutOfStock = true;
  }
  data.customDeliveryTime = customDeliveryTime;

  return data;
};

export const checkSubItemDetails = (
  subItem: SubItemType,
  hasRoundingMethodAndLevel: boolean,
  itemStatus: ItemStatusSliceType
) => {
  const data = {
    subItem: lodashClonedeep(subItem),
    isOutOfStock: false,
  };

  if (!subItem.item) {
    data.isOutOfStock = true;

    return data;
  }

  const { offerItem, isOutOfStock } =
    getItemOfferOutOfStockAndCustomDeliveryTime(itemStatus, subItem.item!.id);
  data.isOutOfStock = isOutOfStock;

  handleSubItemOfferPrice(data.subItem, offerItem, hasRoundingMethodAndLevel);

  return data;
};

export const getAllUnavailableItemIds = (
  itemStatusRedux: ItemStatusSliceType,
  orderType?: OrderTypesEnum
) => {
  const outOfStockItemIds = itemStatusRedux.outOfStockItemsIds || [];

  const disabledItemsIds = itemDetailsHelp.getAllDisabledItems(
    itemStatusRedux,
    orderType
  );

  return {
    disabledItemsIds,
    outOfStockItemIds,
    allUnavailableItemIds: disabledItemsIds.concat(outOfStockItemIds),
  };
};

export const isItemHaveRequiredAddons = (AddonGroups: any) => {
  let isItemHaveRequiredAddons: boolean = false;

  for (let i = 0; i < AddonGroups?.length; i++) {
    const AddonGroup = AddonGroups[i];
    if (AddonGroup.minChoices > 0) {
      isItemHaveRequiredAddons = true;
      break;
    }
  }

  return isItemHaveRequiredAddons;
};

export const isItemHaveAddons = (AddonGroups: any) => {
  return !!(AddonGroups && AddonGroups.length > 0);
};

export const getAllDisabledItems = (
  itemStatusRedux: ItemStatusSliceType,
  orderTypeQueryParam?: OrderTypesEnum
) => {
  const disabledItemsIdsPerFranchise = itemStatusRedux.disabledItemsIds;

  const disabledItemsIdsPerCurrentOrderType = getDisabledItemsPerOrderType(
    itemStatusRedux,
    orderTypeQueryParam
  );

  const allDisabledItemsIds = (disabledItemsIdsPerFranchise ?? []).concat(
    disabledItemsIdsPerCurrentOrderType ?? []
  );

  return allDisabledItemsIds;
};

const toMinutes = (time: string) => {
  const [hours, minutes] = time.split(":").map(Number);

  return hours * 60 + minutes;
};

/**
 * Converts a time string from "HH:mm:ss" (24-hour format) to "hh:mm A" (12-hour format).
 * @param time - The time string in "HH:mm:ss" format.
 * @returns The time string in "hh:mm A" format.
 */
export const formatTimeTo12Hour = (time: string): string => {
  const [hours, minutes] = time.split(":");
  const date = new Date();
  date.setHours(Number(hours), Number(minutes), 0);

  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });
};

/**
 * Checks if the current time is within the specified availability range.
 * @param startTime - The start time in "HH:mm:ss" format.
 * @param endTime - The end time in "HH:mm:ss" format.
 * @param enabled - Whether the availability range is enabled.
 * @param timezone - the restaurant local timezone.
 * @param t - The i18n translation function.
 * @returns An object containing:
 *   - `isAvailable`: A boolean indicating if the item is currently available.
 *   - `availabilityMessage`: A localized string describing the availability in 12-hour format.
 *   - `fullMessage`: A localized string describing start time and end time availability in 12-hour format
 */
export const checkAvailability = (
  startTime: string,
  endTime: string,
  enabled: boolean,
  timezone: string,
  t?: (key: string, values?: Record<string, string>) => string
): {
  isAvailable: boolean;
  fullMessage?: string;
  availabilityMessage?: string;
} => {
  if (!enabled || (!startTime && !endTime)) {
    return { isAvailable: true };
  }

  const now = new Date();
  const currentTime = now.toLocaleTimeString("en-US", {
    timeZone: envConfig.enviroment === "local" ? "Africa/cairo" : timezone,
    hour12: false,
  });

  const formattedStartTime = formatTimeTo12Hour(startTime);
  const formattedEndTime = formatTimeTo12Hour(endTime);

  const fullMessage =
    t &&
    t("common:available-from-to", {
      startTime: formattedStartTime,
      endTime: formattedEndTime,
    });

  const availabilityMessage =
    t &&
    t("common:x-to-z", {
      startTime: formattedStartTime,
      endTime: formattedEndTime,
    });

  const currentMinutes = toMinutes(currentTime);
  const startMinutes = toMinutes(startTime);
  const endMinutes = toMinutes(endTime);

  let isAvailable: boolean;
  if (startMinutes < endMinutes) {
    isAvailable =
      currentMinutes >= startMinutes && currentMinutes <= endMinutes;
  } else {
    if (currentMinutes >= startMinutes || currentMinutes <= endMinutes) {
      isAvailable = true;
    } else {
      isAvailable = false;
    }
  }

  if (!isAvailable) {
    return { isAvailable, fullMessage, availabilityMessage };
  }

  return { isAvailable: true };
};
